const API_BASE =
  import.meta.env.VITE_APP_API_URL || "/api";

const TOKEN_KEY = "excalidraw-auth-token";

function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
}

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };
  if (options.body) {
    headers["Content-Type"] = "application/json";
  }
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${res.status}`);
  }

  return res.json();
}

// --- Types ---

export interface ApiUser {
  id: string;
  email: string;
  name: string | null;
  is_admin?: boolean;
}

export interface TagInfo {
  id: string;
  name: string;
  color: string;
}

export interface FolderInfo {
  id: string;
  parent_id: string | null;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface DrawingMetadata {
  id: string;
  name: string;
  thumbnail: string | null;
  folder_id: string | null;
  tags: TagInfo[];
  created_at: string;
  updated_at: string;
}

export interface DrawingFull extends DrawingMetadata {
  elements: any[];
  app_state: Record<string, any>;
  appState: Record<string, any>;
  files: Record<string, any>;
}

// --- Auth ---

export async function apiRegister(
  email: string,
  password: string,
  name?: string,
): Promise<{ token: string; user: ApiUser }> {
  const result = await request<{ token: string; user: ApiUser }>(
    "/auth/register",
    {
      method: "POST",
      body: JSON.stringify({ email, password, name }),
    },
  );
  setToken(result.token);
  return result;
}

export async function apiLogin(
  email: string,
  password: string,
): Promise<{ token: string; user: ApiUser }> {
  const result = await request<{ token: string; user: ApiUser }>(
    "/auth/login",
    {
      method: "POST",
      body: JSON.stringify({ email, password }),
    },
  );
  setToken(result.token);
  return result;
}

export async function apiGetMe(): Promise<ApiUser> {
  const result = await request<{ user: ApiUser }>("/auth/me");
  return result.user;
}

export function apiLogout() {
  setToken(null);
}

export function isLoggedIn(): boolean {
  return !!getToken();
}

// --- Drawings ---

export async function apiListDrawings(): Promise<DrawingMetadata[]> {
  const result = await request<{ drawings: DrawingMetadata[] }>("/drawings");
  return result.drawings;
}

export async function apiGetDrawing(id: string): Promise<DrawingFull> {
  const result = await request<{ drawing: DrawingFull }>(`/drawings/${id}`);
  return result.drawing;
}

export async function apiCreateDrawing(data: {
  name?: string;
  elements?: any[];
  appState?: Record<string, any>;
  thumbnail?: string;
  folderId?: string | null;
}): Promise<DrawingMetadata> {
  const result = await request<{ drawing: DrawingMetadata }>("/drawings", {
    method: "POST",
    body: JSON.stringify(data),
  });
  return result.drawing;
}

export async function apiUpdateDrawing(
  id: string,
  data: {
    name?: string;
    elements?: any[];
    appState?: Record<string, any>;
    thumbnail?: string;
    folderId?: string | null;
  },
): Promise<DrawingMetadata> {
  const result = await request<{ drawing: DrawingMetadata }>(
    `/drawings/${id}`,
    {
      method: "PUT",
      body: JSON.stringify(data),
    },
  );
  return result.drawing;
}

// Fire-and-forget save that survives page unload (keepalive fetch)
// Browsers limit keepalive request bodies to 64KB, so we strip unnecessary data.
export function apiUpdateDrawingKeepAlive(
  id: string,
  data: { elements?: any[]; appState?: Record<string, any> },
): void {
  const token = getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  // Strip deleted elements and volatile appState to stay under 64KB keepalive limit
  const minimalData: Record<string, any> = {};
  if (data.elements) {
    minimalData.elements = data.elements.filter((el: any) => !el.isDeleted);
  }
  if (data.appState) {
    const {
      cursorButton,
      scrollX,
      scrollY,
      zoom,
      selectedElementIds,
      selectedGroupIds,
      editingGroupId,
      editingElement,
      ...persistableState
    } = data.appState;
    minimalData.appState = persistableState;
  }

  const body = JSON.stringify(minimalData);

  // If payload still exceeds ~60KB, skip keepalive and rely on the last regular sync
  if (body.length > 60000) {
    console.warn(
      "Cloud sync payload too large for keepalive (%d bytes), skipping unload save",
      body.length,
    );
    return;
  }

  fetch(`${API_BASE}/drawings/${id}`, {
    method: "PUT",
    headers,
    body,
    keepalive: true,
  }).catch(() => {}); // fire-and-forget
}

export async function apiDeleteDrawing(id: string): Promise<void> {
  await request(`/drawings/${id}`, { method: "DELETE" });
}

export async function apiDuplicateDrawing(
  id: string,
): Promise<DrawingMetadata> {
  const result = await request<{ drawing: DrawingMetadata }>(
    `/drawings/${id}/duplicate`,
    { method: "POST" },
  );
  return result.drawing;
}

// --- Admin ---

export async function apiAdminListUsers(): Promise<ApiUser[]> {
  const result = await request<{ users: ApiUser[] }>("/admin/users");
  return result.users;
}

export async function apiAdminCreateUser(data: {
  email: string;
  password: string;
  name?: string;
  is_admin?: boolean;
}): Promise<ApiUser> {
  const result = await request<{ user: ApiUser }>("/auth/register", {
    method: "POST",
    body: JSON.stringify(data),
  });
  return result.user;
}

export async function apiAdminUpdateUser(
  id: string,
  data: { name?: string; email?: string; is_admin?: boolean; password?: string },
): Promise<ApiUser> {
  const result = await request<{ user: ApiUser }>(`/admin/users/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
  return result.user;
}

export async function apiAdminDeleteUser(id: string): Promise<void> {
  await request(`/admin/users/${id}`, { method: "DELETE" });
}

// --- Comments ---

export interface ElementComment {
  id: string;
  element_id: string;
  text: string;
  author_name: string | null;
  author_email: string;
  created_at: string;
  updated_at: string;
}

export async function apiListComments(
  drawingId: string,
): Promise<ElementComment[]> {
  const result = await request<{ comments: ElementComment[] }>(
    `/drawings/${drawingId}/comments`,
  );
  return result.comments;
}

export async function apiCreateComment(
  drawingId: string,
  elementId: string,
  text: string,
): Promise<ElementComment> {
  const result = await request<{ comment: ElementComment }>(
    `/drawings/${drawingId}/comments`,
    {
      method: "POST",
      body: JSON.stringify({ elementId, text }),
    },
  );
  return result.comment;
}

export async function apiUpdateComment(
  drawingId: string,
  commentId: string,
  text: string,
): Promise<ElementComment> {
  const result = await request<{ comment: ElementComment }>(
    `/drawings/${drawingId}/comments/${commentId}`,
    {
      method: "PUT",
      body: JSON.stringify({ text }),
    },
  );
  return result.comment;
}

export async function apiDeleteComment(
  drawingId: string,
  commentId: string,
): Promise<void> {
  await request(`/drawings/${drawingId}/comments/${commentId}`, {
    method: "DELETE",
  });
}

// --- Folders ---

export async function apiListFolders(): Promise<FolderInfo[]> {
  const result = await request<{ folders: FolderInfo[] }>("/folders");
  return result.folders;
}

export async function apiCreateFolder(data: {
  name?: string;
  parentId?: string | null;
}): Promise<FolderInfo> {
  const result = await request<{ folder: FolderInfo }>("/folders", {
    method: "POST",
    body: JSON.stringify(data),
  });
  return result.folder;
}

export async function apiUpdateFolder(
  id: string,
  data: { name?: string; parentId?: string | null },
): Promise<FolderInfo> {
  const result = await request<{ folder: FolderInfo }>(`/folders/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
  return result.folder;
}

export async function apiDeleteFolder(id: string): Promise<void> {
  await request(`/folders/${id}`, { method: "DELETE" });
}

// --- Tags ---

export async function apiListTags(): Promise<TagInfo[]> {
  const result = await request<{ tags: TagInfo[] }>("/tags");
  return result.tags;
}

export async function apiCreateTag(data: {
  name: string;
  color?: string;
}): Promise<TagInfo> {
  const result = await request<{ tag: TagInfo }>("/tags", {
    method: "POST",
    body: JSON.stringify(data),
  });
  return result.tag;
}

export async function apiUpdateTag(
  id: string,
  data: { name?: string; color?: string },
): Promise<TagInfo> {
  const result = await request<{ tag: TagInfo }>(`/tags/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
  return result.tag;
}

export async function apiDeleteTag(id: string): Promise<void> {
  await request(`/tags/${id}`, { method: "DELETE" });
}

export async function apiSetDrawingTags(
  drawingId: string,
  tagIds: string[],
): Promise<TagInfo[]> {
  const result = await request<{ tags: TagInfo[] }>(
    `/drawings/${drawingId}/tags`,
    {
      method: "PUT",
      body: JSON.stringify({ tagIds }),
    },
  );
  return result.tags;
}

// --- Shares (view-only links) ---

export interface ShareInfo {
  id: string;
  share_token: string;
  created_at: string;
}

export interface SharedDrawing {
  name: string;
  elements: any[];
  appState: Record<string, any>;
  authorName: string;
  files: Record<string, any>;
}

export async function apiCreateShareLink(
  drawingId: string,
): Promise<ShareInfo> {
  const result = await request<{ share: ShareInfo }>(
    `/drawings/${drawingId}/share`,
    { method: "POST" },
  );
  return result.share;
}

export async function apiRevokeShareLink(drawingId: string): Promise<void> {
  await request(`/drawings/${drawingId}/share`, { method: "DELETE" });
}

export async function apiGetSharedDrawing(
  token: string,
): Promise<SharedDrawing> {
  const res = await fetch(`${API_BASE}/shared/${token}`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${res.status}`);
  }
  const data = await res.json();
  return data.drawing;
}

// --- Files ---

export async function apiSaveFiles(
  drawingId: string,
  files: Array<{ id: string; mimeType: string; dataURL: string }>,
): Promise<void> {
  await request(`/drawings/${drawingId}/files`, {
    method: "PUT",
    body: JSON.stringify({ files }),
  });
}
